/**
 * Product Hooks
 * 
 * React Query hooks for product operations.
 */

'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GetProductBySlugUseCase } from '@/application/use-cases/get-product-by-slug.use-case';
import { AddToCartUseCase } from '@/application/use-cases/add-to-cart.use-case';
import { productService, cartService } from '@/infrastructure/config/services.config';

const getProductBySlugUseCase = new GetProductBySlugUseCase(productService);
const addToCartUseCase = new AddToCartUseCase(cartService);

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const result = await getProductBySlugUseCase.execute({ slug });
      return result.product;
    },
    enabled: !!slug,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });
}

/**
 * Returns the first image URL for a product, fetching from the products API
 * only when a local image_url is not available.
 */
export function useProductImageUrl(productId: string | undefined, existingImageUrl: string | undefined) {
  const shouldFetch = !!productId && !existingImageUrl;
  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const result = await getProductBySlugUseCase.execute({ slug: productId! });
      return result.product;
    },
    enabled: shouldFetch,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (existingImageUrl) return existingImageUrl;
  return product?.images?.[0]?.src ?? undefined;
}

/**
 * Returns { size, color } for a given product variant by looking up
 * variation_attributes from the products API.
 */
export function useVariantAttributes(productId: string | undefined, variationId: string | undefined) {
  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const result = await getProductBySlugUseCase.execute({ slug: productId! });
      return result.product;
    },
    enabled: !!productId && !!variationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  if (!product || !variationId) return { size: undefined, color: undefined };

  const variant = product.variants?.find(
    (v) => v.id === variationId || v.uuid === variationId,
  );
  if (!variant) return { size: undefined, color: undefined };

  const attrs = variant.variation_attributes ?? [];
  const size = attrs.find((a) => a.name.toLowerCase() === 'size')?.value;
  const color = attrs.find((a) => a.name.toLowerCase() === 'color')?.value;
  return { size, color };
}

export function useAddToCart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      cartToken?: string;
      productId: string;
      variantId: string;
      quantity: number;
      subscription?: import('@template-project/storefront-bff-client').CartItemSubscriptionInput;
      options?: {
        countryCode?: string;
        currency?: string;
        currencyRatio?: number;
        vendor?: string;
        shop?: string;
        upsellFunnelId?: string;
        upsellFirstStepId?: string;
      };
    }) => {
      return addToCartUseCase.execute(input);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['cart', data.cartToken] });
    },
  });
}
